import { useMemo } from 'react';

import { useHashtags } from '../../../../../components/recognition/bonus/hooks/use-hashtags';
import { LeaderboardFilters, useLeaderboard, useLeaderboardQueries } from '../../../../bonus-feed/features/recognition/api/hooks/leaderboards';
import { getScopeParams } from '../leaderboard-scope';
import { LeaderboardSummaryExportData } from '../leaderboard-summary-export-bar';
import { areHashtagReceiversReady, isFetchingHashtagReceivers } from '../leaderboard-summary-export-readiness';
import { LeaderboardState } from '../types';
import { useTimeframeDates } from './use-timeframe';

const PREVIEW_LIMIT = 5;

export const useLeaderboardSummaryExportData = (state: LeaderboardState): LeaderboardSummaryExportData => {
	const { startDate, endDate } = useTimeframeDates(state.selectedTimeframe, state.customStartDate, state.customEndDate);
	const scopeParams = useMemo(() => getScopeParams(state), [state.scopeType, state.selectedTeam, state.selectedDepartment]);

	const { data: hashtagData, isLoading: isLoadingHashtags } = useHashtags({
		params: { display_format: 'core_value_hashtags' },
	});

	const companyValues = hashtagData?.data?.result?.values ?? [];
	const hashtags = companyValues.map((value) => {
		const hashtag = value.value.startsWith('#') ? value.value : `#${value.value}`;
		return hashtag.startsWith('#') ? hashtag.slice(1) : hashtag;
	});

	const receivedFilters: LeaderboardFilters = useMemo(
		() => ({
			limit: PREVIEW_LIMIT,
			start_date: startDate,
			end_date: endDate,
			direction: 'received',
			...scopeParams,
		}),
		[startDate, endDate, scopeParams]
	);

	const givenFilters: LeaderboardFilters = useMemo(
		() => ({
			limit: PREVIEW_LIMIT,
			start_date: startDate,
			end_date: endDate,
			direction: 'given',
			...scopeParams,
		}),
		[startDate, endDate, scopeParams]
	);

	const { data: allCompanyData, isLoading: isLoadingAllCompany, isFetching: isFetchingAllCompany } = useLeaderboard(receivedFilters);
	const { data: topGiversData, isLoading: isLoadingTopGivers, isFetching: isFetchingTopGivers } = useLeaderboard(givenFilters);

	const allCompanyUsers = allCompanyData?.users ?? [];
	const topGiversUsers = topGiversData?.users ?? [];

	const isDataSettled = !isLoadingAllCompany && !isFetchingAllCompany && !isLoadingTopGivers && !isFetchingTopGivers && !isLoadingHashtags;
	const isAllEmpty = isDataSettled && allCompanyUsers.length === 0 && topGiversUsers.length === 0;

	const hashtagReceiverFilters = useMemo(
		() =>
			hashtags.map((hashtag) => ({
				hashtag,
				limit: PREVIEW_LIMIT,
				start_date: startDate,
				end_date: endDate,
				...scopeParams,
			})),
		[hashtags, startDate, endDate, scopeParams]
	);

	const hashtagReceiverQueries = useLeaderboardQueries(hashtagReceiverFilters);

	const hashtagReceivers = useMemo(
		() => Object.fromEntries(hashtags.map((hashtag, index) => [hashtag, hashtagReceiverQueries[index]?.data?.users ?? []] as const)),
		[hashtags, hashtagReceiverQueries]
	);

	const hashtagReceiverQueryStatus = useMemo(
		() => hashtagReceiverQueries.map((query) => ({ isLoading: query.isLoading, isFetching: query.isFetching })),
		[hashtagReceiverQueries]
	);

	const areHashtagReceiversReadyForExport = areHashtagReceiversReady(hashtags, hashtagReceiverQueryStatus, isLoadingHashtags);
	const isFetchingHashtagReceiverData = isFetchingHashtagReceivers(hashtags, hashtagReceiverQueryStatus, isLoadingHashtags);
	const isFetching = isFetchingAllCompany || isFetchingTopGivers || isFetchingHashtagReceiverData;
	const isSummaryExportReady = isDataSettled && areHashtagReceiversReadyForExport && !isAllEmpty;

	return {
		isReady: isSummaryExportReady,
		isFetching,
		startDate,
		endDate,
		scopeType: state.scopeType,
		selectedTimeframe: state.selectedTimeframe,
		hashtagCount: hashtags.length,
		receivedUsers: allCompanyUsers,
		givenUsers: topGiversUsers,
		hashtagReceivers,
		hashtags,
		scopeParams,
	};
};
